/**
 * Registers the Chabad Digital service worker so the app becomes installable as
 * a PWA. Registration only runs in production builds (served over HTTPS or
 * localhost) — the SW is skipped during `vue-cli-service serve` and any existing
 * worker is unregistered there so it can't serve stale assets while developing.
 */

// vue-cli exposes the deploy base path here; the SW must live at the app scope.
const BASE_URL = process.env.BASE_URL || '/';

export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  if (process.env.NODE_ENV !== 'production') {
    // Dev: make sure no previously-installed SW keeps serving cached files.
    navigator.serviceWorker.getRegistrations?.().then((regs) => {
      regs.forEach((reg) => reg.unregister());
    });
    // Drop what it cached too. Unregistering stops the worker but leaves its
    // caches on disk, so a developer who once loaded a production build on
    // localhost would otherwise keep that build's assets sitting in storage.
    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.filter((k) => k.startsWith('cd-')).map((k) => caches.delete(k))))
        .catch(() => {
          /* best effort only */
        });
    }
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${BASE_URL}service-worker.js`;
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        // Surface when a new version has been installed in the background.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // A newer build is ready; it will take over on the next visit.
              window.dispatchEvent(new CustomEvent('cd:sw-updated', { detail: registration }));
            }
          });
        });
      })
      .catch((err) => {
        console.warn('[PWA] Service worker registration failed:', err);
      });
  });
}
